/**
 * The Parkour Documents tab: what is actually WRITTEN against this revision.
 *
 * TWO KINDS OF DOCUMENT NOW SIT ON ONE RECORD, and they are not the same thing. A form-based
 * document answers a shape -- its fields are on the Form tab and they are t1's. A Parkour Doc is
 * authored prose, written in Docs, and a revision has as many as it needs: a procedure, its
 * appendices, a training summary. Files attached to the record are a third thing again and keep
 * their own tab. This tab is the prose.
 *
 * READ-ONLY HERE, DELIBERATELY. Editing happens in Docs, which asks this tier on every write
 * whether the revision may change at all. A second editing path in QMS would be a second way into
 * the same rows with nobody asking -- and the collaboration, the suggestions and the digest fence
 * all live in the editor, not in a text box bolted onto a tab.
 *
 * NO CUSTOM FIELDS. Ed, 2026-10-02: "No custom fields will be editable in t11." The division is the
 * other way round too -- a Parkour Doc is not edited here, and a form answer is not edited there.
 *
 * THE THUMBNAIL IS THE DOCUMENT, not a picture of it, matching the cards in Docs exactly. Rendering
 * page images needs a pipeline, somewhere to keep them, and something to notice when a document
 * changes and throw the old one away. The opening text is already here and cannot go stale.
 */
import React, {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Card, CardActionArea, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";
import {AddOutlined, DescriptionOutlined, FileCopyOutlined, OpenInNew} from "@mui/icons-material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {buildSsoHandoffUrl, handoffVisibility} from "../../util/ssoAuthenticate";
import {Grey5, Grey6, Grey17, Blue3} from "../../theme/styles";

type ParkourDoc = {
    USID: string;
    Name: string;
    Sequence: number | null;
    Origin: string;
    OpenSuggestions: number;
    Source: string;
    SourceFilename: string | null;
    Preview: string;
    HasBody: boolean;
    Characters: number;
    LastModifiedDatetime: string | null;
};

const DOCS_URL = process.env.REACT_APP_DOCS_URL ?? "";

export default function ParkourDocuments({objectRevisionUSID, sessionContext}: {
    objectRevisionUSID: string;
    sessionContext: any;
}) {
    const [documents, setDocuments] = useState<ParkourDoc[] | null>(null);
    const [editable, setEditable] = useState(false);
    const [lockedBecause, setLockedBecause] = useState<string | null>(null);
    const [problem, setProblem] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const api = makeCresAxiosFoundationAPI(sessionContext);
            const response = await api.get("t1/v1/parkour_documents",
                                           {params: {RevisionUSID: objectRevisionUSID}});
            setDocuments(response?.data?.Results ?? []);
            setEditable(response?.data?.Editable === true);
            setLockedBecause(response?.data?.Reason ?? null);
            setProblem(null);
        } catch (error: any) {
            setProblem(error?.response?.data?.Cause || error?.response?.data?.Code
                       || "The documents could not be loaded.");
            setDocuments([]);
        }
    }, [objectRevisionUSID, sessionContext]);

    useEffect(() => { load(); }, [load]);

    /**
     * Opens Docs, signed in and reading as the persona in use here.
     *
     * THE TOKEN CROSSES so the target can mint its own session: a Docs URL without it lands on a
     * sign-in page, which for somebody already signed in reads as the link being broken.
     *
     * AND SO DOES THE PERSONA, which the suite's hand-off deliberately does NOT do in general --
     * the target's Positions are its own and are not these. Docs is the exception, and a checkable
     * one: it loads an account's positions with the same query this tier does and forwards
     * VisibilityMode and VisibilityValue straight back to QMS for the document list, the search and
     * every lifecycle action. The USID is literally the same row. Without it, somebody who pressed
     * Create would be stopped by a persona chooser before reaching the editor -- which is the
     * question they just answered here.
     */
    const launch = (path: string) => {
        if (!DOCS_URL) return;
        const token = sessionContext?.valid?.() ? sessionContext.getAccessToken() : null;
        window.open(buildSsoHandoffUrl(`${DOCS_URL}${path}`, token, handoffVisibility(sessionContext)),
                    "_blank", "noopener,noreferrer");
    };

    const open = (usid: string) => launch(`/document/${objectRevisionUSID}/${usid}`);

    // CREATE MAKES THE DOCUMENT IN DOCS, not here, and lands in the editor. The name is the
    // server's next free "Document N" rather than a dialog: asking what to call something before
    // it has a word in it is a question nobody answers well, and renaming is one click in the
    // editor once there is something to name.
    //
    // ALSO WHY THIS TAB DOES NOT CREATE IT ITSELF. Every write to a Parkour Doc goes through t11,
    // which asks this tier on each one whether the revision may change. A create path here would be
    // a second way into the same rows with nobody asking.
    const create = () => launch(`/document/${objectRevisionUSID}?create=1`);

    // AND THE SAME HAND-OFF FOR STARTING FROM ONE THAT EXISTS. The picker is in Docs, not here,
    // for the reason above: every write to a Parkour Doc goes through t11, and QMS choosing what
    // t11 then copies would put half of one decision on the other side of that line. This says
    // which revision to write to and what the reader came to do; Docs asks which document.
    //
    // A COPY, NEVER A LINK. One document belongs to one revision -- the column is NOT NULL and
    // there is no link table -- so two records cannot share one, and they should not: the
    // revision's lifecycle is what decides whether its text may change, and two records cannot
    // both hold that. What arrives is a duplicate, independently revisable from the moment it
    // lands, with its figures duplicated too.
    const createFromExisting = () => launch(`/document/${objectRevisionUSID}?from=1`);

    if (documents === null) {
        return <Box sx={{display: "flex", justifyContent: "center", p: 6}}><CircularProgress/></Box>;
    }

    return (
        <Box sx={{marginTop: 3}}>
            {problem && <Alert severity="info" sx={{mb: 2}}>{problem}</Alert>}

            <Box sx={{display: "flex", alignItems: "center", mb: 2, gap: 1.5}}>
                {/* OFFERED ONLY WHILE THE REVISION IS OPEN, mirroring the Attachments tab: starting
                    a document on a released revision would change what was released. Hiding it is
                    not the enforcement -- t11 asks again on the write -- it only avoids offering a
                    change that would be refused after a new tab had already opened. */}
                <Button variant="contained" size="small" startIcon={<AddOutlined/>}
                        disabled={!editable || !DOCS_URL} onClick={create}>
                    Create
                </Button>
                {/* GATED THE SAME WAY, and for the same reason: starting a document on a released
                    revision would change what was released. t11 asks again on the write; this only
                    avoids offering a change that would be refused after a tab had opened. */}
                <Button variant="outlined" size="small" startIcon={<FileCopyOutlined/>}
                        disabled={!editable || !DOCS_URL} onClick={createFromExisting}>
                    Create from existing document
                </Button>
                {!editable && lockedBecause && (
                    // THE SERVER'S OWN SENTENCE for why, rather than a disabled button with no
                    // explanation -- "a DRAFT revision with an approval outstanding" is not
                    // something a reader can work out from a greyed control.
                    <Typography sx={{fontSize: 12, color: Grey6}}>{lockedBecause}</Typography>
                )}
            </Box>

            {documents.length === 0 ? (
                // NOT AN ERROR AND NOT AN EMPTY BOX. Most records have no Parkour Doc -- a document
                // managed as a file, or as form answers, is a complete document. The message says
                // what this tab is for rather than implying something is missing.
                <Alert severity="info">
                    Nothing has been authored here yet. A Parkour Doc is the electronic text of this
                    document, written and reviewed in Docs; this record may equally be managed as
                    form answers or as an attached file.
                </Alert>
            ) : (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 2}}>
                    {documents.map(one => (
                        <Card key={one.USID} variant="outlined"
                              sx={{width: 190, borderRadius: 2, overflow: "hidden"}}>
                            <CardActionArea onClick={() => open(one.USID)}>
                                <Box sx={{
                                    height: 196, backgroundColor: "#FFFFFF", display: "flex",
                                    flexDirection: "column", borderBottom: "1px solid",
                                    borderColor: "divider", overflow: "hidden",
                                }}>
                                    <Box sx={{
                                        flexGrow: 1, p: 1.25, overflow: "hidden",
                                        // Real text at a size you read SHAPE from rather than
                                        // words, which is what a thumbnail is for.
                                        fontSize: "5.5px", lineHeight: 1.45, color: Grey17,
                                        whiteSpace: "pre-wrap",
                                    }}>
                                        {one.HasBody && one.Preview ? one.Preview : (
                                            <Box sx={{height: "100%", display: "flex",
                                                      flexDirection: "column", alignItems: "center",
                                                      justifyContent: "center", gap: 0.5}}>
                                                <DescriptionOutlined sx={{fontSize: 28, color: "#C9CBD2"}}/>
                                                <Typography sx={{fontSize: 10, color: Grey5}}>
                                                    No content yet
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </CardActionArea>

                            <Box sx={{px: 1.25, pt: 1, pb: 0.5, display: "flex", alignItems: "center", gap: 0.5}}>
                                <Tooltip title={one.Name}>
                                    <Typography sx={{fontSize: 13, fontWeight: 500, color: Grey17,
                                                     overflow: "hidden", textOverflow: "ellipsis",
                                                     whiteSpace: "nowrap"}}>
                                        {one.Name}
                                    </Typography>
                                </Tooltip>
                                <Box sx={{flexGrow: 1}}/>
                                <Tooltip title="Open in Docs">
                                    <OpenInNew onClick={() => open(one.USID)}
                                               sx={{fontSize: 15, color: Blue3, cursor: "pointer", flexShrink: 0}}/>
                                </Tooltip>
                            </Box>

                            <Box sx={{px: 1.25, pb: 1, display: "flex", alignItems: "center", gap: 0.75}}>
                                {/* INHERITED IS WORTH SAYING. The text was carried across when this
                                    revision was raised rather than written on it, and "where did
                                    this come from" is an inspection question the words cannot
                                    answer. DIRECT is the ordinary case and says nothing. */}
                                {one.Origin === "INHERITED" && (
                                    <Tooltip title="Carried across from the previous revision">
                                        <Chip size="small" label="Inherited" variant="outlined"
                                              sx={{height: 20, fontSize: 11}}/>
                                    </Tooltip>
                                )}
                                {!!one.OpenSuggestions && (
                                    <Tooltip title={`${one.OpenSuggestions} tracked change(s) to resolve before this can be submitted`}>
                                        <Chip size="small" label={one.OpenSuggestions} variant="outlined"
                                              sx={{height: 20, fontSize: 11}}/>
                                    </Tooltip>
                                )}
                                {one.SourceFilename && (
                                    <Tooltip title={`Imported from ${one.SourceFilename}`}>
                                        <Typography sx={{fontSize: 11, color: Grey6, overflow: "hidden",
                                                         textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                            {one.SourceFilename}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Box>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
}
